import { Heart, GitCompareArrows, Check, TrendingDown } from "lucide-react";
import { useCompare } from "@/context/CompareContext";
import { useFavorites } from "@/context/FavoritesContext";
import { cardImage } from "@/lib/img";
import { isNew } from "@/lib/isNew";
import { Link } from "react-router-dom";
import SmartImage from "@/components/SmartImage";
import ShareButton from "@/components/ShareButton";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.floor((Date.now() - d.getTime()) / 1000);
  const map = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60]];
  for (const [unit, s] of map) {
    const v = Math.floor(secs / s);
    if (v >= 1) return `${v} ${unit}${v > 1 ? "s" : ""} ago`;
  }
  return "just now";
}

export default function DealCard({ deal, index = 0, enableCompare = false }) {
  const { has, toggle } = useCompare();
  const { hasDeal, toggleDeal } = useFavorites();
  const selected = has(deal.id);
  const saved = hasDeal(deal.id);
  const fresh = isNew(deal.created_at);
  return (
    <article
      data-testid="deal-card"
      className="group bg-white rounded-xl border border-neutral-200 overflow-hidden flex flex-col hover:shadow-lg hover:-translate-y-1 transition-[transform,box-shadow] duration-200 fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-50 p-3">
        <Link to={`/deal/${deal.id}`} data-testid="deal-detail-link" aria-label={`See details: ${deal.title}`} className="block w-full h-full">
          {deal.image_url ? (
            <SmartImage src={cardImage(deal.image_url, 600)} alt={deal.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-300 font-display text-4xl">{deal.store_name || "Deal"}</div>
          )}
        </Link>
        <div className="absolute top-3 left-3 flex items-center gap-2">
          {deal.unavailable ? (
            <span className="bg-neutral-700 text-white text-xs font-bold px-2.5 py-1 rounded-md" data-testid="deal-ended">Deal ended</span>
          ) : deal.discount_percent > 0 && (
            <span className="bg-brand text-white text-xs font-bold px-2.5 py-1 rounded-md">-{deal.discount_percent}%</span>
          )}
          {fresh && (
            <span className="bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-sm inline-flex items-center gap-1" data-testid="new-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> New
            </span>
          )}
          {deal.price_dropped && (
            <span className="bg-green-600 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-sm inline-flex items-center gap-1" data-testid="deal-price-drop">
              <TrendingDown className="w-3 h-3" /> Price dropped
            </span>
          )}
        </div>
        <div className="absolute bottom-3 right-14">
          <ShareButton path={`/deal/${deal.id}`} title={`${deal.title}${deal.price ? ` — ${deal.price}` : ""}`} image={deal.image_url} testid="deal-share-btn" />
        </div>
        <button
          data-testid="deal-fav-btn"
          onClick={(e) => { e.preventDefault(); toggleDeal(deal); }}
          className={`absolute bottom-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors ${saved ? "bg-brand/10 hover:bg-brand/20" : "bg-blue-50 hover:bg-blue-100"}`}
          aria-label={saved ? "Remove from saved deals" : "Save deal"}
        >
          <Heart className={`w-4 h-4 transition-colors ${saved ? "fill-brand text-brand" : "text-blue-400"}`} />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        {deal.is_hot && (
          <span className="self-start bg-ink-950 text-white text-[11px] font-bold px-2.5 py-1 rounded-md mb-2 tracking-wide">Exclusive</span>
        )}
        <a href={deal.affiliate_url} target="_blank" rel="noopener noreferrer sponsored" data-testid="deal-title-link"
          className="font-display font-bold text-ink-900 leading-snug text-[17px] hover:text-brand transition-colors line-clamp-2">
          {deal.title} {deal.price && <span className="text-brand">{deal.price}</span>}
        </a>
        <p className="text-sm text-neutral-600 leading-relaxed mt-2 line-clamp-4">{deal.description}</p>

        <div className="flex items-center justify-between gap-2 mt-4 pt-1">
          <a data-testid="deal-card-cta" href={deal.affiliate_url} target="_blank" rel="noopener noreferrer sponsored"
            data-aff-title={deal.title} data-aff-kind="deal"
            className="inline-flex items-center justify-center h-9 px-5 rounded-full bg-brand/10 hover:bg-brand text-brand hover:text-white font-semibold text-sm transition-colors">
            View Deal
          </a>
          {enableCompare ? (
            <button
              data-testid="deal-compare-toggle"
              aria-pressed={selected}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(deal); }}
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-xs font-bold border transition-colors ${selected ? "bg-brand text-white border-brand" : "bg-white text-ink-800 border-neutral-200 hover:border-brand hover:text-brand"}`}
            >
              {selected ? <Check className="w-3.5 h-3.5" /> : <GitCompareArrows className="w-3.5 h-3.5" />}
              {selected ? "Added" : "Compare"}
            </button>
          ) : (
            <span className="text-xs text-neutral-400 whitespace-nowrap">{timeAgo(deal.created_at)}</span>
          )}
        </div>
        {enableCompare && <span className="text-xs text-neutral-400 mt-2" data-testid="deal-time-ago">{timeAgo(deal.created_at)}</span>}
      </div>
    </article>
  );
}
